import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Search, BookOpen, ArrowRight, Eye, MessageCircle, HelpCircle, Loader2, Star, Files } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  useCustomerPortalCollections,
  useCustomerPortalPopularArticles,
  useCustomerPortalFeaturedArticles,
  useCustomerPortalCollectionArticles,
  useCustomerPortalSearch,
  portalArticleParam,
  recordPortalSearchClick,
  UNCATEGORIZED_COLLECTION,
} from "../../hooks/useKnowledgeBase";
import type { CustomerPortalArticleDto } from "../../types/articles";
import type { PortalCollection } from "../../types/articleCollections";
import { CollectionIcon } from "../../components/CollectionIcon";
import { CustomerPortalKnowledgeBaseSkeleton } from "../../components/LoadingSkeleton";
import { checkCustomerAuthentication, type OrganizationInfo } from "../../lib/portal-loaders";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import { PortalArticleCard } from "../components/PortalArticleCard";
import { KnowledgeBaseSearchBox } from "../components/KnowledgeBaseSearchBox";
import { usePrivateKnowledgeBaseRedirect } from "../utils/knowledgeBaseAccess";

interface PublicKnowledgeBaseProps {
  organizationInfo?: OrganizationInfo;
  organizationId?: string | null;
  // A collection or section id (?collection=)
  collectionFilter?: string;
  // The search in the URL (?search=) - results always follow it, so a search is shareable and Back works
  searchFilter?: string;
}

export const PublicKnowledgeBase: React.FC<PublicKnowledgeBaseProps> = ({
  organizationInfo,
  collectionFilter,
  searchFilter,
}) => {
  // What's typed; the search that ran is the URL's
  const [searchInput, setSearchInput] = useState(searchFilter || "");
  const navigate = useNavigate();
  const orgSlug = organizationInfo?.subdomain ?? "";
  const {
    getContrastText,
    getThemedPrimaryColor,
    getThemedSecondaryColor,
    getThemedPrimaryWithOpacity,
  } = usePortalBranding();
  // The portal's "Other articles": published articles filed in no collection
  const isUncategorized = collectionFilter === UNCATEGORIZED_COLLECTION;

  // Following a link or Back to another search shows it in the box
  useEffect(() => {
    setSearchInput(searchFilter || "");
  }, [searchFilter]);

  const activeSearch = (searchFilter ?? "").trim();
  const isSearching = activeSearch.length > 0;

  const {
    data: collectionsData,
    isLoading: collectionsLoading,
    error: collectionsError,
  } = useCustomerPortalCollections();
  const collections = collectionsData?.collections;

  // The selected collection, or a section and the collection it's in
  const selected = (() => {
    if (!collectionFilter || !collections) return null;
    for (const collection of collections) {
      if (collection.id === collectionFilter) return { collection, section: null };
      const section = collection.sections.find((s) => s.id === collectionFilter);
      if (section) return { collection, section };
    }
    return null;
  })();

  const { data: popularArticles, isLoading: popularLoading, error: popularError } = useCustomerPortalPopularArticles(4);
  const { data: featuredArticles } = useCustomerPortalFeaturedArticles();

  // A collection's articles, sections included, in its curated order - a page at a time
  const collectionQuery = useCustomerPortalCollectionArticles(isSearching ? undefined : collectionFilter);
  const collectionArticles = collectionQuery.data?.pages.flatMap((p) => p.articles) ?? [];
  const collectionTotal = collectionQuery.data?.pages[0]?.totalCount ?? 0;

  // Group a collection's articles under its sections: its own first, then each section in order
  const articleGroups = (() => {
    if (!selected || selected.section) return [{ id: collectionFilter ?? "", title: null as string | null, articles: collectionArticles }];
    const groups = [{ id: selected.collection.id, title: null as string | null, articles: collectionArticles.filter((a) => a.collectionId === selected.collection.id) }];
    for (const section of selected.collection.sections) {
      groups.push({ id: section.id, title: section.name, articles: collectionArticles.filter((a) => a.collectionId === section.id) });
    }
    return groups.filter((g) => g.articles.length > 0);
  })();

  // Search across the whole knowledge base, best match first, a page at a time
  const searchQuery = useCustomerPortalSearch(activeSearch);
  const searchResults = searchQuery.data?.pages.flatMap((p) => p.articles) ?? [];
  const searchTotal = searchQuery.data?.pages[0]?.totalCount ?? 0;
  // The logged search, so opening a result counts in the search insights
  const searchId = searchQuery.data?.pages[0]?.searchId;

  const runSearch = (query: string) => {
    if (query && orgSlug) {
      navigate({
        to: "/org/$orgSlug/portal/knowledge-base",
        params: { orgSlug },
        // A search covers the whole knowledge base, so it leaves the collection
        search: { search: query },
      });
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (orgSlug) {
      navigate({
        to: "/org/$orgSlug/portal/knowledge-base",
        params: { orgSlug },
        search: collectionFilter ? { collection: collectionFilter } : {},
      });
    }
  };

  // A private knowledge base sends visitors who aren't signed in to sign in, and back here afterwards
  const isKbPublic = organizationInfo?.portalSettings?.isKnowledgeBasePublic ?? true;
  const isAuthenticated = checkCustomerAuthentication(organizationInfo?.organizationId);
  const redirecting = usePrivateKnowledgeBaseRedirect(organizationInfo, isKbPublic, isAuthenticated);

  if (redirecting) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Show loading state for initial load
  if (collectionsLoading && !isSearching && !collectionFilter) {
    return <CustomerPortalKnowledgeBaseSkeleton />;
  }

  const loadingSpinner = (label: string) => (
    <div className="flex items-center justify-center py-16" role="status">
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: getThemedPrimaryColor() }} />
      <span className="ml-2 text-gray-600 dark:text-gray-400">{label}</span>
    </div>
  );

  const showMore = (hasNextPage: boolean, isFetchingNextPage: boolean, fetchNextPage: () => void, shown: number, total: number) =>
    hasNextPage && (
      <div className="flex flex-col items-center gap-1 pt-2">
        <Button variant="outline" onClick={fetchNextPage} disabled={isFetchingNextPage}>
          {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Show more articles
        </Button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Showing {shown} of {total}
        </span>
      </div>
    );

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Hero Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center">
            <div
              className="mx-auto mb-4 sm:mb-6 h-12 w-12 sm:h-16 sm:w-16 rounded-full flex items-center justify-center"
              style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1) }}
            >
              <BookOpen className="h-6 w-6 sm:h-8 sm:w-8" style={{ color: getThemedPrimaryColor() }} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 px-4">
              How can we help you?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto px-4">
              Search our knowledge base for answers to common questions, or browse by topic below.
            </p>

            {/* Search Bar - suggests articles as the visitor types */}
            <KnowledgeBaseSearchBox value={searchInput} onChange={setSearchInput} onSearch={runSearch} orgSlug={orgSlug} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            {isSearching ? (
              // Search Results
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Search Results
                    {searchQuery.data && (
                      <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-normal text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">
                        ({searchTotal} {searchTotal === 1 ? "result" : "results"} for "{activeSearch}")
                      </span>
                    )}
                  </h3>
                  <Button variant="ghost" onClick={handleClearSearch} className="w-full sm:w-auto">
                    ← Back to Browse
                  </Button>
                </div>

                {searchQuery.isLoading ? (
                  loadingSpinner("Searching...")
                ) : searchQuery.isError ? (
                  <Alert>
                    <AlertDescription>The search couldn't be completed. Please try again.</AlertDescription>
                  </Alert>
                ) : searchResults.length > 0 ? (
                  <div className={`space-y-4 transition-opacity ${searchQuery.isPlaceholderData ? "opacity-60" : ""}`}>
                    {searchResults.map((article: CustomerPortalArticleDto) => (
                      <PortalArticleCard
                        key={article.id}
                        article={article}
                        orgSlug={orgSlug}
                        onOpen={() => recordPortalSearchClick(searchId, article.id)}
                      />
                    ))}
                    {showMore(searchQuery.hasNextPage, searchQuery.isFetchingNextPage, () => searchQuery.fetchNextPage(), searchResults.length, searchTotal)}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No articles found</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Try different words, browse our topics, or ask our support team.
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <Button variant="ghost" onClick={handleClearSearch}>
                        Browse Topics
                      </Button>
                      <Button variant="outline" asChild>
                        <Link to="/org/$orgSlug/portal/submit-ticket" params={{ orgSlug }}>
                          <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                          Submit a Ticket
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : collectionFilter ? (
              // A collection (with its sections) or a single section
              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <div className="min-w-0">
                    {selected?.section && (
                      <Link
                        to="/org/$orgSlug/portal/knowledge-base"
                        params={{ orgSlug }}
                        search={{ collection: selected.collection.id }}
                        className="text-sm hover:underline mb-1 inline-block"
                        style={{ color: getThemedPrimaryColor() }}
                      >
                        {selected.collection.name}
                      </Link>
                    )}
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      {selected?.section?.name || selected?.collection.name || (isUncategorized ? "Other articles" : "Articles")}
                      {collectionQuery.data && (
                        <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-normal text-gray-500 dark:text-gray-400 mt-1 sm:mt-0">
                          ({collectionTotal} {collectionTotal === 1 ? "article" : "articles"})
                        </span>
                      )}
                    </h3>
                    {(selected?.section?.description || (!selected?.section && selected?.collection.description)) && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selected?.section ? selected.section.description : selected?.collection.description}
                      </p>
                    )}
                  </div>
                  <Button variant="ghost" asChild className="w-full sm:w-auto">
                    <Link to="/org/$orgSlug/portal/knowledge-base" params={{ orgSlug }}>
                      ← Back to Browse
                    </Link>
                  </Button>
                </div>

                {collectionQuery.isLoading ? (
                  loadingSpinner("Loading articles...")
                ) : collectionQuery.isError ? (
                  <Alert>
                    <AlertDescription>This topic's articles couldn't be loaded. Please try again.</AlertDescription>
                  </Alert>
                ) : articleGroups.some((g) => g.articles.length > 0) ? (
                  <div className="space-y-6">
                    {articleGroups.map((group) => (
                      <section key={group.id} className="space-y-4" aria-label={group.title ?? undefined}>
                        {group.title && (
                          <h4
                            className="text-base sm:text-lg font-semibold border-b pb-2"
                            style={{ color: getThemedPrimaryColor(), borderColor: getThemedPrimaryWithOpacity(0.2) }}
                          >
                            {group.title}
                          </h4>
                        )}
                        {group.articles.map((article: CustomerPortalArticleDto) => (
                          <PortalArticleCard key={article.id} article={article} orgSlug={orgSlug} showCollection={false} />
                        ))}
                      </section>
                    ))}
                    {showMore(collectionQuery.hasNextPage, collectionQuery.isFetchingNextPage, () => collectionQuery.fetchNextPage(), collectionArticles.length, collectionTotal)}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No articles here yet</h4>
                    <p className="text-gray-600 dark:text-gray-400">This topic doesn't have any published articles yet.</p>
                    <Button variant="ghost" asChild className="mt-4">
                      <Link to="/org/$orgSlug/portal/knowledge-base" params={{ orgSlug }}>
                        Browse All Topics
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              // Normal browse content
              <>
                {/* Featured articles - the ones staff marked as the place to start */}
                {featuredArticles && featuredArticles.length > 0 && (
                  <section className="mb-8" aria-labelledby="featured-articles-heading">
                    <h3
                      id="featured-articles-heading"
                      className="flex items-center gap-2 text-xl sm:text-2xl font-bold mb-4"
                      style={{ color: getThemedPrimaryColor() }}
                    >
                      <Star className="h-5 w-5" aria-hidden="true" />
                      Featured Articles
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {featuredArticles.slice(0, 6).map((article) => (
                        <li key={article.id}>
                          <Link
                            to="/org/$orgSlug/portal/knowledge-base/$articleId"
                            params={{ orgSlug, articleId: portalArticleParam(article) }}
                            className="block h-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                            style={{ "--tw-ring-color": getThemedPrimaryColor() } as React.CSSProperties}
                          >
                            <span className="block font-semibold text-sm sm:text-base hover:underline" style={{ color: getThemedPrimaryColor() }}>
                              {article.title}
                            </span>
                            {article.summary && (
                              <span className="mt-1 block text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{article.summary}</span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold" style={{ color: getThemedPrimaryColor() }}>
                    Browse by Topic
                  </h3>
                </div>

                {collectionsLoading ? (
                  loadingSpinner("Loading topics...")
                ) : collectionsError ? (
                  <Alert>
                    <AlertDescription>Unable to load the knowledge base. Please try again later.</AlertDescription>
                  </Alert>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    {collections?.map((collection: PortalCollection) => (
                      // The collection's name is the card's link, stretched over the whole card; the section links sit
                      // above it, so each is its own link rather than a link inside a link
                      <div
                        key={collection.id}
                        className="relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-offset-2"
                        style={{ "--tw-ring-color": getThemedPrimaryColor() } as React.CSSProperties}
                      >
                        <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6">
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                            style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1), color: getThemedPrimaryColor() }}
                          >
                            <CollectionIcon icon={collection.icon} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-semibold mb-1">
                              <Link
                                to="/org/$orgSlug/portal/knowledge-base"
                                params={{ orgSlug }}
                                search={{ collection: collection.id }}
                                className="hover:underline focus-visible:outline-none after:absolute after:inset-0 after:content-['']"
                                style={{ color: getThemedPrimaryColor() }}
                              >
                                {collection.name}
                              </Link>
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                              {collection.description || "Helpful articles on this topic"}
                            </p>
                            {collection.sections.length > 0 && (
                              <ul className="mb-3 space-y-0.5 text-xs text-gray-600 dark:text-gray-400">
                                {collection.sections.slice(0, 3).map((section) => (
                                  <li key={section.id}>
                                    <Link
                                      to="/org/$orgSlug/portal/knowledge-base"
                                      params={{ orgSlug }}
                                      search={{ collection: section.id }}
                                      className="relative z-10 hover:underline"
                                    >
                                      {section.name}
                                    </Link>
                                  </li>
                                ))}
                                {collection.sections.length > 3 && <li>+{collection.sections.length - 3} more</li>}
                              </ul>
                            )}
                            <Badge
                              className="text-xs"
                              style={{
                                backgroundColor: getThemedPrimaryWithOpacity(0.1),
                                color: getThemedPrimaryColor(),
                                border: `1px solid ${getThemedPrimaryWithOpacity(0.2)}`,
                              }}
                            >
                              {collection.articleCount} {collection.articleCount === 1 ? "article" : "articles"}
                            </Badge>
                          </div>
                          <ArrowRight className="h-5 w-5 flex-shrink-0" style={{ color: getThemedPrimaryWithOpacity(0.4) }} aria-hidden="true" />
                        </div>
                      </div>
                    ))}
                    {/* Articles filed in no collection get a card of their own, so browsing reaches them too */}
                    {collectionsData && collectionsData.uncategorizedArticleCount > 0 && (
                      <div
                        className="relative rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-offset-2"
                        style={{ "--tw-ring-color": getThemedPrimaryColor() } as React.CSSProperties}
                      >
                        <div className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6">
                          <div
                            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
                            style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1), color: getThemedPrimaryColor() }}
                          >
                            <Files className="h-5 w-5" aria-hidden="true" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-semibold mb-1">
                              <Link
                                to="/org/$orgSlug/portal/knowledge-base"
                                params={{ orgSlug }}
                                search={{ collection: UNCATEGORIZED_COLLECTION }}
                                className="hover:underline focus-visible:outline-none after:absolute after:inset-0 after:content-['']"
                                style={{ color: getThemedPrimaryColor() }}
                              >
                                Other articles
                              </Link>
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">More articles on other topics</p>
                            <Badge
                              className="text-xs"
                              style={{
                                backgroundColor: getThemedPrimaryWithOpacity(0.1),
                                color: getThemedPrimaryColor(),
                                border: `1px solid ${getThemedPrimaryWithOpacity(0.2)}`,
                              }}
                            >
                              {collectionsData.uncategorizedArticleCount} {collectionsData.uncategorizedArticleCount === 1 ? "article" : "articles"}
                            </Badge>
                          </div>
                          <ArrowRight className="h-5 w-5 flex-shrink-0" style={{ color: getThemedPrimaryWithOpacity(0.4) }} aria-hidden="true" />
                        </div>
                      </div>
                    )}
                    {(!collections || collections.length === 0) && (collectionsData?.uncategorizedArticleCount ?? 0) === 0 && (
                      <div className="col-span-2 text-center py-12">
                        <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                        <p className="text-gray-600 dark:text-gray-400">
                          No articles available yet.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6 sm:space-y-8">
            {/* Popular Articles */}
            <Card>
              <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: getThemedPrimaryColor() }} aria-hidden="true" />
                  <span style={{ color: getThemedPrimaryColor() }}>Popular Articles</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                {popularLoading ? (
                  <div className="flex items-center justify-center py-8" role="status">
                    <Loader2 className="h-6 w-6 animate-spin" style={{ color: getThemedPrimaryColor() }} />
                    <span className="ml-2 text-gray-600 dark:text-gray-400">Loading...</span>
                  </div>
                ) : popularError ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600 dark:text-gray-400 text-sm">Unable to load popular articles</p>
                  </div>
                ) : popularArticles && popularArticles.length > 0 ? (
                  <ol className="space-y-2">
                    {popularArticles.map((article: CustomerPortalArticleDto, index: number) => (
                      <li key={article.id}>
                        <Link
                          to="/org/$orgSlug/portal/knowledge-base/$articleId"
                          params={{ orgSlug, articleId: portalArticleParam(article) }}
                          className="flex items-start gap-2 sm:gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 p-2 sm:p-3 rounded-md transition-colors"
                        >
                          <span
                            className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-semibold"
                            style={{ backgroundColor: getThemedPrimaryWithOpacity(0.1), color: getThemedPrimaryColor() }}
                            aria-hidden="true"
                          >
                            {index + 1}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-medium text-gray-900 dark:text-white text-xs sm:text-sm line-clamp-2">
                              {article.title}
                            </span>
                            <span className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                              <Eye className="mr-1 h-3 w-3" aria-hidden="true" />
                              {article.viewCount?.toLocaleString() || "0"} views
                            </span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <div className="text-center py-8">
                    <Eye className="h-8 w-8 text-gray-400 mx-auto mb-2" aria-hidden="true" />
                    <p className="text-gray-600 dark:text-gray-400 text-sm">No popular articles yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Need More Help */}
            <Card>
              <CardHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <HelpCircle className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                  <span>Need More Help?</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <h5 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                      Can't find what you're looking for?
                    </h5>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Submit a support ticket and our team will get back to you.
                    </p>
                    <Button
                      asChild
                      className="w-full"
                      style={{
                        backgroundColor: getThemedPrimaryColor(),
                        borderColor: getThemedPrimaryColor(),
                        color: getContrastText(getThemedPrimaryColor()),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = getThemedSecondaryColor();
                        e.currentTarget.style.borderColor = getThemedSecondaryColor();
                        e.currentTarget.style.color = getContrastText(getThemedSecondaryColor());
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = getThemedPrimaryColor();
                        e.currentTarget.style.borderColor = getThemedPrimaryColor();
                        e.currentTarget.style.color = getContrastText(getThemedPrimaryColor());
                      }}
                    >
                      <Link to="/org/$orgSlug/portal/submit-ticket" params={{ orgSlug }}>
                        <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                        Submit Support Ticket
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
